import { useSyncExternalStore } from "react";

// Chrome, Edge and Android offer to install TWT with one tap of their own
// install sheet. The offer arrives as beforeinstallprompt shortly after load;
// it is held here so "Turn on alerts" can show it at the moment the member
// asks. Imported by WebAppHead so the listener is in place on every member
// page before the event fires.
type InstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let held: InstallPromptEvent | null = null;
let installedHere = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    held = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    held = null;
    installedHere = true;
    notify();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useInstallPrompt() {
  const available = useSyncExternalStore(
    subscribe,
    () => held !== null,
    () => false
  );
  const installed = useSyncExternalStore(
    subscribe,
    () => installedHere,
    () => false
  );
  return { available, installed };
}

/** Shows the browser's install sheet once. */
export async function promptInstall(): Promise<
  "accepted" | "dismissed" | "unavailable"
> {
  const event = held;
  if (!event) return "unavailable";
  held = null;
  notify();
  try {
    await event.prompt();
    return (await event.userChoice).outcome;
  } catch {
    return "unavailable";
  }
}
